import clsx from "clsx";
import { useFormik } from "formik";
import moment from "moment/moment";
import { Chip } from "primereact/chip";
import { Tag } from "primereact/tag";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import DataTableWrapper from "../../components/DataTableWrapper";
import DatePicker from "../../components/DatePicker";
import DotBall from "../../components/DotBall";
import Dropdown from "../../components/Dropdown";
import Loader from "../../components/Loader";
import PaddingContainer from "../../components/PaddingContainer";
import PageHeading from "../../components/PageHeading";
import Popover from "../../components/Popovers";
import SearchInput from "../../components/SearchInput";
import StatusIndicator from "../../components/StatusIndicator";
import TableValue from "../../components/TableValue";
import TabsContainer from "../../components/TabsContainer";
import {
  AddCircleIcon,
  CalendarGridIcon,
  CalendarIcon,
  DownloadCsvIcon,
  DownloadIcon,
  DownloadPDFIcon,
  InvoiceIcon,
  ReceivePaymentIcon,
  RefreshIcon,
  RefundCancelIcon,
  RemoveFilterIcon,
  ViewDetailIcon,
  CancelAndRefundIcon,
  CompletedIcon,
} from "../../constant";
import useDebounce from "../../CustomHooks/useDebounce";
import { paidOptions, statusOptions } from "../../data";
import {
  addMonths,
  formatNumberWithCommas,
  formattedDate,
  formatText,
} from "../../helpers";
import { exportBookingsToExcel } from "../../helpers/exportHelpers";
import AreYouSureModal from "../../modals/AreYouSureModal";
import AddAndRefundModal from "../../modals/Bookings/AddAndRefund";
import AddOrEditBookingModal from "../../modals/Bookings/AddOrEditBookingModal";
import PaymentReceivedModal from "../../modals/Bookings/PaymentReceived";
import RescheduleBookingModal from "../../modals/Bookings/ReschduleBooking";
import SlotsTableModal from "../../modals/Bookings/SlotsTableModal";
import { exportDetailsToPDF } from "../../pdfs/bookings/bookingDetailPdf";
import { exportBookingListingToPDF } from "../../pdfs/bookings/bookingListing";
import AreYouSureWantToCompleteModal from "../../modals/AreYouSureWantToCompleted";
import {
  clearSelectDate,
  setLastVisitedPage,
  setPagination,
  setSearchTerm,
  setSortState,
  updateFilter,
} from "../../store/bookingFilterSlice";
import { setPageLink, setPageTitle } from "../../store/pageSlice";
import useApi from "../../useApi";
import classes from "./Booking.module.css";

// Add sorting fields enum at the top
const BookingSortFields = {
  DateTime: 1,
  CourtName: 3,
  Amount: 4,
  BookingStatus: 5,
  BookingReference: 6,
};

const formattedDateHandler = ({
  bookingDate,
  bookingStartTime,
  bookingEndTime,
}) => {
  // Convert to time range (1-hour slot)
  const startTime = moment(bookingStartTime, "HH:mm:ss").format("hh:mm A"); // "04:00 PM"
  const endTime = moment(bookingEndTime, "HH:mm:ss").format("hh:mm A");

  return (
    <TableValue
      top={formattedDate(bookingDate)}
      down={`${startTime} - ${endTime}`}
    />
  );
};

const RoleBodyTemplate = ({ role }) => <Chip label={role} />;
RoleBodyTemplate.propTypes = {
  role: PropTypes.string.isRequired,
};

const RoleStatusBodyTemplate = ({ roleStatus }) => (
  <Tag
    value={roleStatus}
    severity={roleStatus === "Active" ? "success" : "warning"}
  />
);

RoleStatusBodyTemplate.propTypes = {
  roleStatus: PropTypes.string.isRequired,
};

const RoleTypeBodyTemplate = ({ roleType }) => <Chip label={roleType} />;

RoleTypeBodyTemplate.propTypes = {
  roleType: PropTypes.string.isRequired,
};

const statusMenuItems = (rowData, onCancel, onConfirm) => [
  {
    icon: <DotBall bookingStatus={1} />,
    label: "Confirm",
    command: () => {
      onConfirm(rowData);
    },
  },
  {
    icon: <DotBall bookingStatus={2} />,
    label: "Cancel",
    command: () => {
      onCancel(rowData);
    },
  },
];
const statusMenuItemsForComplete = (rowData, onCancel, onConfirm) => [
  {
    icon: <DotBall bookingStatus={4} />,
    label: "Completed",
    command: () => {
      onConfirm(rowData);
    },
  },
  {
    icon: <DotBall bookingStatus={2} />,
    label: "Cancel",
    command: () => {
      onCancel(rowData);
    },
  },
];
const columns = (
  items,
  setSelectedItem,
  navigate,
  onAddAndRefund,
  onReceivePayment,
  onBookingCancel,
  onBookingConfirm,
  setRefundCancelModalType,
  onRescheduleBooking,
  onCompleted
) => [
  {
    field: "bookingNo",
    header: "Reference ID",
    sortable: true,
    sortField: BookingSortFields.BookingReference,
    body: (rowData) => {
      return (
        <TableValue top={rowData?.bookingNo?.split("-")?.slice(1).join("-")} />
      );
    },
  },
  {
    field: "bookingDate",
    header: "Date & Time",
    sortable: true,
    sortField: BookingSortFields.DateTime,
    body: (rowData) => {
      return formattedDateHandler({
        bookingDate: rowData?.bookingDate,
        bookingStartTime: rowData?.bookingStartTime,
        bookingEndTime: rowData?.bookingEndTime,
      });
    },
  },

  {
    field: "name",
    header: "Name",
    body: (rowData) => (
      <>
        <TableValue
          top={
            rowData.bookingPlayers?.find((data) => data?.isPrimary)
              ?.playerFullName
          }
          down={`0${rowData.bookingPlayers
            ?.find((data) => data?.isPrimary)

            ?.playerCellNumber?.slice(3)}`.replace(/(\d{4})(\d{7})/, "$1 $2")}
        />
        {rowData?.corporateName && (
          <p className={classes.corporateName}>{rowData?.corporateName}</p>
        )}
      </>
    ),
  },
  {
    field: "courtName",
    header: "Court Name",
    sortable: true,
    sortField: BookingSortFields.CourtName,
    body: (rowData) => (
      <TableValue top={rowData?.courtName} down={rowData?.location} />
    ),
  },
  {
    field: "bookingNetAmount",
    header: "Amount",
    sortable: true,
    sortField: BookingSortFields.Amount,
    body: (rowData) => (
      <TableValue
        top={formatNumberWithCommas(rowData?.bookingNetAmount)}
        down={`Collected: ${formatNumberWithCommas(
          rowData?.bookingCollectedAmount
        )}`}
      />
    ),
  },
  {
    field: "status",
    header: "Booking Status",
    sortable: true,
    sortField: BookingSortFields.BookingStatus,
    body: (rowData) => {
      let menuItems = [];
      if (rowData?.bookingStatusId === 3) {
        menuItems = statusMenuItems(
          rowData,
          () => onBookingCancel(rowData),
          () => onBookingConfirm(rowData)
        );
      } if(rowData?.bookingStatusId===1){
          menuItems = statusMenuItemsForComplete(
          rowData,
          () => onBookingCancel(rowData),
          () => onCompleted(rowData)
        );
      }

      return (
        <StatusIndicator
          bookingStatus={rowData?.bookingStatusId}
          menuItems={menuItems}
          // onConfirm={() => {
          //   onCompleted(rowData);
          // }}
          // bookingDate={rowData?.bookingDate}
          // bookingEndTime={rowData?.bookingEndTime}
        />
      );
    },
  },
  {
    field: "actions",
    header: "Actions",
    body: (rowData) => {
      const currentDateTime = moment();
      const bookingDateTime = moment(
        `${rowData?.bookingDate} ${rowData?.bookingStartTime}`
      );
      const isFutureBooking = bookingDateTime.isAfter(currentDateTime);
      const isPaymentComplete =
        rowData?.bookingCollectedAmount === rowData?.bookingNetAmount;

      const items = [
        {
          label: "View Detail",
          icon: <ViewDetailIcon className={classes.menuIcon} />,
          command: () => {
            navigate(`/bookings/${rowData.bookingId}`);
          },
        },
        !isPaymentComplete && {
          label: "Receive Payment",
          icon: <ReceivePaymentIcon className={classes.menuIcon} />,
          command: () => {
            if (
              rowData?.bookingStatusId !== 2 &&
              rowData?.bookingStatusId !== 3
            ) {
              onReceivePayment(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },
        rowData?.bookingStatusId === 1 && {
          label: "Complete Booking",
          icon: <CompletedIcon  className={classes.completedIcon}/>,
          command: () => {
            if (rowData?.bookingStatusId == 1) {
              onCompleted(rowData);
            }
          },
        },
        rowData?.bookingStatusId === 1 &&
          isFutureBooking && {
            label: "Reschedule",
            icon: <CalendarIcon className={classes.menuIcon} />,
            command: () => {
              if (
                rowData?.bookingStatusId !== 2 &&
                rowData?.bookingStatusId !== 3
              ) {
                onRescheduleBooking(rowData);
              }
            },
            disabled: [2, 3].includes(rowData?.bookingStatusId),
          },
        // rowData?.bookingStatusId === 1 && isFutureBooking && {
        rowData?.bookingStatusId === 1 && {
          label: "Refund",
          icon: <CancelAndRefundIcon className={classes.menuIcon} />,
          command: () => {
            if (
              rowData?.bookingStatusId !== 2 &&
              rowData?.bookingStatusId !== 3
            ) {
              setRefundCancelModalType("refund");
              onAddAndRefund(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },
        rowData?.bookingStatusId === 1 && {
          label: "Refund/Cancel",
          icon: <RefundCancelIcon className={classes.menuIcon} />,
          command: () => {
            if (
              rowData?.bookingStatusId !== 2 &&
              rowData?.bookingStatusId !== 3
            ) {
              setRefundCancelModalType("");
              onAddAndRefund(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },
        {
          label: "Download PDF",
          icon: <DownloadPDFIcon className={classes.menuIcon} />,
          command: () => {
            if (![2, 3].includes(rowData?.bookingStatusId)) {
              exportDetailsToPDF(rowData);
            }
          },
          disabled: [2, 3].includes(rowData?.bookingStatusId),
        },
      ]?.filter(Boolean);

      return <Popover items={items} />;
    },
  },
];
const initialValues = {
  status: null,
  paid: null,
  locations: null,
  court: null,

  selectDate: [
    {
      startDate: new Date(),
      endDate: new Date(addMonths(6)),
      key: "selection",
    },
  ],
};

export default function Booking() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dataTableRef = useRef(null); // Add ref for DataTable
  const [showSlotsTableModal, setShowSlotsTableModal] = useState(false);
  const [showBookingCalendarModal, setShowBookingCalendarModal] =
    useState(false);
  // Get booking filters from Redux
  const bookingFilter = useSelector((state) => state?.bookingFilter);
  const { allPreLoadedApis: preloadedApis } = useSelector((state) => state);

  const [showAddOrEditBookingModal, setShowAddOrEditBookingModal] =
    useState(false);
  const [showRefundAndCancelModal, setShowRefundAndCancelModal] =
    useState(false);
  const [showPaymentReceiveModal, setShowPaymentReceiveModal] = useState(false);
  const [showCancelBookingModal, setShowCancelBookingModal] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [download, setDownload] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [showAreYouSureCompletedModal, setShowAreYouSureCompletedModal] =
    useState(false);
  // Use filter values from Redux
  const [first, setFirst] = useState(bookingFilter.first);

  const [search, setSearch] = useState(bookingFilter.search);
  const { sortState } = useSelector((state) => state.bookingFilter);
  const [rows, setRows] = useState(bookingFilter.rows);
  const [refundCancelModalType, setRefundCancelModalType] = useState("");
  const [showRescheduleBookingModal, setShowRescheduleBookingModal] =
    useState(false);
  const [newBooking, setNewBooking] = useState(null);
  const [options, setOptions] = useState({
    status: statusOptions,
    paid: paidOptions,
    court: [
      ...[{ courtName: "All", courtId: 0 }],
      ...(preloadedApis?.courts || []),
    ]?.map((data) => ({
      label: data?.courtName,
      value: data?.courtId,
    })),
    locations: [
      ...(preloadedApis?.locations?.length > 1
        ? [{ locationName: "All", locationId: 0 }]
        : []),
      ...(preloadedApis?.locations || []),
    ]?.map((data) => ({
      label: data?.locationName,
      value: data?.locationId,
    })),
  });
  const debouncedValue = useDebounce(search, 500);

  // Set up API hooks
  const {
    getBookingsAdmin,
    getCourts,
    getAdminLocations,
    getLocations,
    getAllBlockedSlots,
    addBooking,
    refundAndCancel,
    paymentReceive,
    confirmBooking,
    rescheduleBooking,
    bookingCompleted,
  } = useApi();

  // Set up formik with redux values
  const formik = useFormik({
    initialValues: {
      status: bookingFilter.status,
      paid: bookingFilter.paid,
      court: bookingFilter.court,
      locations: bookingFilter.locations,
      selectDate: bookingFilter.selectDate,
    },
    onSubmit: () => {},
  });

  const { values, setFieldValue } = formik;

  // Handle filter change and update Redux
  const handleChange = (filterName) => (value) => {
    setFieldValue(filterName, value);
    dispatch(updateFilter({ filterName, value }));
  };

  const updateFirst = (newFirst) => {
    setFirst(newFirst);
    dispatch(
      setPagination({
        page: Math.floor(newFirst / rows) + 1,
        first: newFirst,
      })
    );
  };

  // Handle page change
  const handlePageChange = (e) => {
    console.log("page change e", e);
    updateFirst(e.first);
    if (e.rows !== rows) {
      setRows(e.rows);
    }
    getBookings({
      ...getBookingParams(),
      pageNumber: e.page + 1,
      pageSize: e.rows,
    });
  };
  // Handle search change
  const handleSearchChange = (value) => {
    setSearch(value);
    dispatch(setSearchTerm(value));
  };

  // Get parameters for booking API calls
  const getBookingParams = () => {
    let startDate = values?.selectDate?.[0]?.startDate;
    let endDate = values?.selectDate?.[0]?.endDate;

    let sortStateOrder = sortState?.order || -1;

    return {
      search: search,
      pageNumber: Math.floor(first / rows) + 1,
      pageSize: rows,
      bookingDateStart: startDate
        ? moment(startDate)?.format("YYYY-MM-DD")
        : null,
      bookingDateEnd: endDate ? moment(endDate)?.format("YYYY-MM-DD") : null,
      locationId: values?.locations || null,
      courtId: values?.court || null,
      bookingStatusId: values?.status || null,
      isPaid: values?.paid === 0 ? null : values?.paid ?? null,
      isDesc: sortStateOrder === -1,
      bookingAdminSortId: sortState?.field || 6,
    };
  };

  // Main function to get bookings data
  const getBookings = async (params) => {
    setLoading(true);
    try {
      const result = await getBookingsAdmin(params);
      if (result?.data?.headers?.error === 200) {
        setBookings(result?.data?.body?.items);
        setTotalCount(result?.data?.body?.totalItems);
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error(new Error(error)?.message);
    }
    setLoading(false);
  };

  // Use this explicit type for localSortMeta to match PrimeReact's requirements
  const [localSortMeta, setLocalSortMeta] = useState({
    sortField: sortState.field,
    sortOrder: sortState.order,
  });

  // Modified handleSort function to ensure proper DataTable interaction
  const handleSort = (event) => {
    // Update local state for immediate UI feedback
    setLocalSortMeta({
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    });

    // Dispatch to Redux for persistence
    dispatch(
      setSortState({
        field: event.sortField,
        order: event.sortOrder,
      })
    );

    // Get booking params with the updated sort parameters
    getBookings({
      ...getBookingParams(),
      isDesc: event.sortOrder === -1,
      bookingAdminSortId: event.sortField || 6,
    });
  };

  // Download options for export dropdown
  const downloadOptions = [
    {
      label: "CSV",
      value: "csv",
      icon: <DownloadCsvIcon />,
    },
    {
      label: "PDF",
      value: "pdf",
      icon: <DownloadPDFIcon />,
    },
  ];

  // Download handler
  const handleDownload = (type) => {
    if (!type) return;

    if (type === "csv") {
      exportBookingsToExcel(bookings, "bookings");
    } else if (type === "pdf") {
      exportBookingListingToPDF(bookings, "bookings");
    }

    setDownload(null);
  };

  // Track page navigation to handle filter state
  useEffect(() => {
    // Mark that we're on the booking page
    dispatch(setLastVisitedPage("booking"));
    // Set page title and navigation
    dispatch(setPageTitle("Bookings"));
    dispatch(setPageLink(""));

    // Set default sort state in Redux
    setSortState({
      field: 6, // BookingSortFields.BookingReference
      order: -1, // -1 for descending order
    });

    // IMPORTANT: Update localSortMeta to show the sort icon
    setLocalSortMeta({
      sortField: 6, // BookingSortFields.BookingReference
      sortOrder: -1, // -1 for descending order
    });

    // Initialize with the correct sort parameters
    // getBookings({
    //   ...getBookingParams(),
    //   bookingAdminSortId: 6, // BookingSortFields.BookingReference
    //   isDesc: true, // Descending order
    // });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // When bookingFilter changes or component mounts, load data with sort state
  useEffect(() => {
    getBookings(getBookingParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedValue,
    values.status,
    values.paid,
    values.court,
    values.locations,
    values.selectDate,

    // Don't include sortState here to prevent double loading
  ]);

  useEffect(() => {
    console.log("preloadedApis?.locations", preloadedApis?.locations);
    setOptions({
      ...options,
      court: values?.locations
        ? [
            ...[{ courtName: "All", courtId: 0 }],
            ...(preloadedApis?.locations?.find(
              (data) => data?.locationId === values?.locations
            )?.courts || []),
          ]?.map((data) => ({
            label: data?.courtName,
            value: data?.courtId,
          }))
        : [
            ...[{ courtName: "All", courtId: 0 }],
            ...(preloadedApis?.courts || []),
          ]?.map((data) => ({
            label: data?.courtName,
            value: data?.courtId,
          })),
    });
  }, [values?.locations]);

  // Define action items for the row actions
  const actionItems = [
    {
      label: "View Detail",
      icon: <ViewDetailIcon />,
      command: (rowData) => {
        navigate(`/bookings/${rowData?.bookingId}`, {
          state: { bookingData: rowData },
        });
      },
    },
    {
      label: "Receive Payment",
      icon: <ReceivePaymentIcon />,
      command: (rowData) => {
        setSelectedItem(rowData);
        setNewBooking(null);
        setShowPaymentReceiveModal(true);
      },
    },
    {
      label: "Invoice",
      icon: <InvoiceIcon />,
      command: (rowData) => {
        window.open(
          `${process.env.REACT_APP_BASE_URL}/api/booking-admin/export-invoice/${
            rowData?.bookingId
          }?token=${localStorage.getItem("token")}`,
          "_blank"
        );
      },
    },
    {
      label: "Refund & Cancel",
      icon: <RefundCancelIcon />,
      command: (rowData) => {
        setSelectedItem(rowData);
        setNewBooking(null);
        setShowRefundAndCancelModal(true);
      },
    },
  ];

  // Handle various booking actions
  const handleAdd = () => setShowAddOrEditBookingModal(true);
  const handleDeleteMultiple = () => console.log("Delete multiple");

  const handleAddAndRefund = (data) => {
    setSelectedItem(data);
    setShowRefundAndCancelModal(true);
  };

  const onReceivePayment = (data) => {
    setSelectedItem(data);
    setShowPaymentReceiveModal(true);
  };

  const onBookingCancel = (data) => {
    setSelectedItem(data);
    setShowCancelBookingModal(true);
  };

  const onBookingConfirm = (data) => {
    setSelectedItem(data);
    setShowPaymentConfirm(true);
  };
  const onRescheduleBooking = (data) => {
    console.log("data onRescheduleBooking", data);
    setSelectedItem(data);
    setShowRescheduleBookingModal(true);
  };

  const onCompleted = (data) => {
    setSelectedItem(data);
    setShowAreYouSureCompletedModal(true);
  };

  // API functions for modal submissions
  const addBookingAdmin = async (data) => {
    setLoading(true);
    try {
      const result = await addBooking(data);
      if (result?.data?.headers?.error === 200) {
        toast.success("Booking added successfully");
        setShowAddOrEditBookingModal(false);
        await getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to add booking");
    }
    setLoading(false);
  };

  const cancelAndRefundApi = async (data) => {
    setLoading(true);
    try {
      const result = await refundAndCancel({
        ...data,
        cancelBooking: false,
        ...(refundCancelModalType !== "refund" && { cancelBooking: true }),
      });
      if (result?.data?.headers?.error === 200) {
        toast.success(
          `${
            refundCancelModalType !== "refund" ? "Cancelled" : "Refunded"
          } Successfully`
        );
        setShowRefundAndCancelModal(false);
        setShowCancelBookingModal(false);
        await getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to refund/cancel");
    }
    setLoading(false);
  };

  const handleCompletedBooking = async (data) => {
    setLoading(true);
    const response = await bookingCompleted(data);
    if (response?.data?.headers?.error === 200) {
      toast.success("Booking Completed Successfully");
      setShowAreYouSureCompletedModal(false);
      getBookings(getBookingParams());
    } else {
      toast.error(response?.data?.headers?.message);
    }
    setLoading(false);
  };

  console.log("SelectedItem", selectedItem);

  const paymentReceiveApi = async (data) => {
    setLoading(true);
    try {
      const result = await paymentReceive(data);
      if (result?.data?.headers?.error === 200) {
        toast.success(result?.data?.headers?.message);
        setShowPaymentReceiveModal(false);
        await getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to receive payment");
    }
    setLoading(false);
  };

  const confirmBookingApi = async (data) => {
    setLoading(true);
    try {
      const result = await confirmBooking({
        bookingId: data?.bookingId,
      });
      if (result?.data?.headers?.error === 200) {
        toast.success(result?.data?.headers?.message);
        setShowPaymentConfirm(false);
        await getBookings(getBookingParams());
      } else {
        toast.error(result?.data?.headers?.message);
      }
    } catch (error) {
      toast.error("Failed to confirm booking");
    }
    setLoading(false);
  };

  const rescheduleBookingApi = async (data) => {
    setLoading(true);
    const result = await rescheduleBooking(data);
    if (result?.data?.headers?.error === 200) {
      toast.success(result?.data?.headers?.message);
      setShowRescheduleBookingModal(false);
      await getBookings(getBookingParams());
    } else {
      toast.error(result?.data?.headers?.message);
    }
    setLoading(false);
  };

  // Add reset filters function
  const resetAllFilters = () => {
    formik.resetForm();
    // Reset search
    setSearch("");
    dispatch(setSearchTerm(""));

    // Reset sort
    dispatch(
      setSortState({
        field: 6, // BookingSortFields.BookingReference
        order: -1, // -1 for descending order
      })
    );
    setLocalSortMeta({
      sortField: 6,
      sortOrder: -1,
    });

    // Reset pagination
    setFirst(0);
    dispatch(
      setPagination({
        page: 1,
        first: 0,
      })
    );

    // Get bookings with reset filters
    getBookings({
      ...getBookingParams(),
      pageNumber: 1,
      pageSize: rows,
      bookingAdminSortId: 6,
      isDesc: true,
    });
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <PaddingContainer>
            <ol className={classes.titleStrip}>
              <li className={classes.titleContainer}>
                <PageHeading label="Booking List" />
                <Button
                  leftIcon={<RefreshIcon />}
                  className={classes.refreshButton}
                  onClick={() => {
                    getBookings(getBookingParams());
                  }}
                />
              </li>
              <li>
                <div className={classes.priorityActions}>
                  {/* <Button
                    className={classes.calendarButton}
                    leftIcon={<CalendarGridIcon />}
                    label="Booking Calendar"
                    onClick={() => {
                      setShowBookingCalendarModal(true);
                    }}
                  /> */}

                  <Button
                    className={classes.calendarButton}
                    leftIcon={<CalendarGridIcon />}
                    label="Booking Table"
                    onClick={() => {
                      setSelectedTimeSlot(null);
                      setShowSlotsTableModal(true);
                    }}
                  />

                  <Dropdown
                    className={classes.bgWhite}
                    placeholder={"Export"}
                    icon={<DownloadIcon />}
                    options={downloadOptions}
                    value={download}
                    onChange={(e) => {
                      setDownload(e);
                      handleDownload(e);
                    }}
                  />
                  <Button
                    leftIcon={<AddCircleIcon />}
                    label="Add Booking"
                    onClick={() => {
                      setSelectedTimeSlot(null);
                      setNewBooking(null);
                      setShowAddOrEditBookingModal(true);
                    }}
                  />
                </div>
              </li>
            </ol>

            <div className={classes.filterActions}>
              <div className={classes.tabsWrapper}>
                <TabsContainer
                  tabs={
                    <>
                      {Object.keys(initialValues)
                        .slice(0, -1)
                        ?.map((filter) => {
                          if (
                            filter === "locations" &&
                            options?.locations?.length <= 1
                          ) {
                            return null;
                          }
                          return (
                            <Dropdown
                              key={filter}
                              placeholder={formatText(filter)}
                              className={clsx(
                                classes.filterAction,
                                classes.bgWhite
                              )}
                              value={values[filter]}
                              onChange={handleChange(filter)}
                              options={options[filter]}
                            />
                          );
                        })}
                      <div className={classes.combineFilter}>
                        <div className={classes.datePickerContainer}>
                          <DatePicker
                            type="range"
                            value={values?.selectDate}
                            onChange={handleChange("selectDate")}
                          />
                          {values?.selectDate?.[0]?.startDate && (
                            <button
                              type="button"
                              className={classes.resetDateButton}
                              onClick={() => {
                                setFieldValue("selectDate", [
                                  {
                                    startDate: null,
                                    endDate: null,
                                    key: "selection",
                                  },
                                ]);
                                dispatch(clearSelectDate());
                              }}
                            >
                              <RxCross2 className={classes.resetIcon} />
                            </button>
                          )}
                        </div>
                        <Button
                          leftIcon={<RemoveFilterIcon />}
                          className={classes.refreshButton}
                          onClick={resetAllFilters}
                        />
                      </div>
                    </>
                  }
                />
              </div>
              <div className={classes.searchContainer}>
                <SearchInput
                  placeholder={"Search by Reference ID/Name/Phone No"}
                  onChange={handleSearchChange}
                  value={search}
                />
              </div>
            </div>
            <DataTableWrapper
              ref={dataTableRef}
              search={search}
              data={bookings}
              columns={columns(
                actionItems,
                setSelectedItem,
                navigate,
                handleAddAndRefund,
                onReceivePayment,
                onBookingCancel,
                onBookingConfirm,
                setRefundCancelModalType,
                onRescheduleBooking,
                onCompleted
              )}
              onAdd={handleAdd}
              onDeleteMultiple={handleDeleteMultiple}
              loading={loading}
              totalRecords={totalCount}
              onPageChange={handlePageChange}
              first={first}
              rows={rows}
              onSort={handleSort}
              sortField={localSortMeta.sortField}
              sortOrder={localSortMeta.sortOrder}
              rowsPerPageOptions={[5, 10, 20, 50]}
            />
          </PaddingContainer>

          {/* Modals remain unchanged */}
          {showAddOrEditBookingModal && (
            <AddOrEditBookingModal
              data={selectedTimeSlot || newBooking}
              isApiCall={loading}
              newBooking={newBooking}
              show={showAddOrEditBookingModal}
              setShow={setShowAddOrEditBookingModal}
              onSubmit={addBookingAdmin}
            />
          )}
          {showRefundAndCancelModal && (
            <AddAndRefundModal
              title={`${
                refundCancelModalType === "refund"
                  ? "Refund"
                  : "Refund And Cancel"
              } Booking`}
              type={refundCancelModalType}
              show={showRefundAndCancelModal}
              setShow={setShowRefundAndCancelModal}
              data={selectedItem}
              onSubmit={cancelAndRefundApi}
            />
          )}
          {showPaymentReceiveModal && (
            <PaymentReceivedModal
              show={showPaymentReceiveModal}
              setShow={setShowPaymentReceiveModal}
              data={selectedItem}
              onSubmit={paymentReceiveApi}
            />
          )}

          {showCancelBookingModal && selectedItem && (
            <AreYouSureModal
              show={showCancelBookingModal}
              setShow={setShowCancelBookingModal}
              data={selectedItem?.bookingNo?.split("-")?.slice(1).join("-")}
              onConfirm={() => {
                cancelAndRefundApi({
                  bookingId: selectedItem?.bookingId,
                  bookingStatusId: null,
                  refundAmount: 0,
                  reason: "Cancelled by Admin",
                  attachment: null,
                });
              }}
            />
          )}

          {showPaymentConfirm && (
            <PaymentReceivedModal
              label="Confirmation"
              show={showPaymentConfirm}
              setShow={setShowPaymentConfirm}
              data={selectedItem}
              onSubmit={confirmBookingApi}
            />
          )}

          {showSlotsTableModal && (
            <SlotsTableModal
              data={{
                locations: preloadedApis?.locations,
              }}
              newBooking={newBooking}
              setNewBooking={setNewBooking}
              setShowAnotherModal={setShowAddOrEditBookingModal}
              setSelectedTimeSlot={setSelectedTimeSlot}
              show={showSlotsTableModal}
              setShow={setShowSlotsTableModal}
            />
          )}

          {showRescheduleBookingModal && (
            <RescheduleBookingModal
              subTitle={`Reference No: ${selectedItem?.bookingNo
                ?.split("-")
                ?.slice(1)
                .join("-")}`}
              show={showRescheduleBookingModal}
              setShow={setShowRescheduleBookingModal}
              data={selectedItem}
              onSubmit={rescheduleBookingApi}
              newBooking={newBooking}
              setNewBooking={setNewBooking}
            />
          )}

          {showAreYouSureCompletedModal && (
            <AreYouSureWantToCompleteModal
              loading={loading}
              show={showAreYouSureCompletedModal}
              data={selectedItem?.bookingNo?.split("-")?.slice(1).join("-")}
              setShow={setShowAreYouSureCompletedModal}
              onConfirm={() => {
                handleCompletedBooking({ bookingId: selectedItem?.bookingId });
              }}
            />
          )}
        </>
      )}
    </>
  );
}
